import { getAppName } from 'src/utils/app';
import { BeaconFilterFieldTypeSchema, FilterFieldConceptSchema, FilterOperatorSchema, FrequencySchema } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchema';
import {
  AtlasGlobalFilterType,
  BeaconGlobalFilterType,
  CreateAlertConfigType,
  FormAlertConfigType,
  SelectedProductType
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchemaTypes';
import { useFormBrands, useFormCategories, useFormRetailers } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/hooks/useCategoricalFilters';
import _ from 'lodash';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';

/**
 * This template will need to be updated to include the client data on the template.
 */
export const DEFAULT_ALERT_TEMPLATE: Partial<CreateAlertConfigType> = {
  alertName: '',
  alertDescription: '',
  queryJSON: {
    basicInfo: {
      frequency: FrequencySchema.enum.weekly,
      granularity: [],
    },
    rules: {
      ruleGroups: [
        {
          ruleBlocks: [
            {
              metric: 'Total Traffic',
              aggregation: 'sum',
              timeConfiguration: {
                mode: 'Predefined Period',
                timeRange: 'Last Week'
              },
              categoricalfilters: []
            }
          ],
          operators: []
        }
      ],
      groupOperators: []
    }
  },
  notifications: {
    methods: [],
    config: {}
  }
};

export const useDefaultGlobalFilters = () => {
  const appName = getAppName();
  const categories = useFormCategories();
  const retailers = useFormRetailers();
  const brands = useFormBrands();

  if (appName === 'atlas') {
    return [
      {
        type: BeaconFilterFieldTypeSchema.enum['Category'],
        concept: FilterFieldConceptSchema.enum['categoryid'],
        operator: 'IN',
        values: categories
      },
      {
        type: BeaconFilterFieldTypeSchema.enum['Retailer'],
        concept: FilterFieldConceptSchema.enum['retailerid'],
        operator: 'IN',
        values: retailers
      }
    ] as AtlasGlobalFilterType[]
  }
  const beaconGlobalFilters: BeaconGlobalFilterType[] = [
    {
      type: BeaconFilterFieldTypeSchema.enum['Brand'],
      concept: FilterFieldConceptSchema.enum['brandid'],
      operator: 'IN',
      values: brands
    },
    {
      type: BeaconFilterFieldTypeSchema.enum['Retailer'],
      concept: FilterFieldConceptSchema.enum['retailerid'],
      operator: 'IN',
      values: retailers
    }
  ]

  return beaconGlobalFilters;
}

/**
 * This hook is used to apply the client sepcific data to the template.
 */
export const useClientDataOnTemplate = (partialConfig: Partial<CreateAlertConfigType>) => {
  const appName = getAppName();
  const defaultGlobalFilters = useDefaultGlobalFilters();
  const myEmail = useAppSelector((state) => state.user.config.profile.email);

  const clientData: RefinedFormConfigCtx = {
    appName,
    defaultGlobalFilters,
    myEmail
  }

  // Type assertion needed because CreateAlertConfigType and FormAlertConfigType are similar but not identical
  const result = addClientDataToCreateConfig(partialConfig as Partial<FormAlertConfigType>, clientData);

  return result;
}

interface RefinedFormConfigCtx {
  appName: string;
  defaultGlobalFilters: BeaconGlobalFilterType[] | AtlasGlobalFilterType[];
  myEmail: string;
}

/**
 * Merge client data with the template config.
 * If some filters and properties are missing, they will be added.
 * If some filters and properties are present, they will be kept.
 */
export function addClientDataToCreateConfig(
  partialConfig: Partial<FormAlertConfigType>,
  clientData: RefinedFormConfigCtx
): FormAlertConfigType {
  // work on a copy
  const config = _.cloneDeep(partialConfig);

  // 1) Apply base defaults without overwriting existing values
  _.defaultsDeep(config, {
    queryJSON: {
      basicInfo: {
        selectedProduct: clientData.appName as SelectedProductType,
        frequency: FrequencySchema.enum.weekly,
      },
      globalFilters: clientData.defaultGlobalFilters,
    },
    notifications: {
      methods: ["Email"],
      config: { email: { recipients: [clientData.myEmail] } },
    },
  });

  // 2) If globalFilters exist but contain any empty values, replace with defaults
  const filters = _.get(config, "queryJSON.globalFilters") as
    | BeaconGlobalFilterType[]
    | undefined;

  if (_.some(filters, (filter) => _.isEmpty(filter?.values))) {
    // Only add filters that have missing or empty values
    const updatedFilters = filters.map(filter => {
      if (_.isEmpty(filter?.values)) {
        // Find matching default filter by type/concept
        const defaultFilter = clientData.defaultGlobalFilters.find(df =>
          df.type === filter.type && df.concept === filter.concept
        );
        return defaultFilter || filter;
      }
      return filter;
    });

    _.set(config, "queryJSON.globalFilters", updatedFilters);
  }

  // 3) Ensure Email method + recipient if notifications existed but were partial
  const methods = _.get(config, "notifications.methods") as string[] | undefined;
  if (methods === undefined || (Array.isArray(methods) && methods.length === 0)) {
    _.set(config, "notifications.methods", ["Email"]);
  } else if (!_.includes(methods, "Email")) {
    _.set(config, "notifications.methods", [...methods, "Email"]);
  }
  const recipients = _.get(config, "notifications.config.email.recipients");
  if (recipients === undefined || (Array.isArray(recipients) && recipients.length === 0)) {
    _.set(config, "notifications.config.email.recipients", [clientData.myEmail]);
  }

  // 4) If frequency is not set, set it to weekly
  if (!_.get(config, "queryJSON.basicInfo.frequency")) {
    _.set(config, "queryJSON.basicInfo.frequency", FrequencySchema.enum.weekly);
  }

  // 5) Adding variant to the config
  config.variant = 'create';

  return config;
}
